import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { incidentsApi } from '../services/incidentsApi';
import { zonesApi, type Zone } from '../services/zonesApi';
import { fetchSiteConditionState, type SiteConditionState } from '../domain/continuousSafety/siteConditionState';
import {
  assembleLiveNowSnapshot,
  envShowcaseLiveDefaults,
  membershipFromProbe,
  resolveThresholdSeconds,
  selectSiteConditionForBoothScope,
  showcaseIdentityConfigKey,
  showcaseMembershipScopeKey,
  type ShowcaseConditionIdentity,
  type ShowcaseLiveNowConfig,
  type ShowcaseLiveNowSnapshot,
  type ShowcaseMembershipRead,
} from '../domain/showcase/liveNowAdapter';

export const SHOWCASE_LIVE_POLL_MS = 10000;
export const SHOWCASE_MEMBERSHIP_POLL_MS = 1000;
export const MAX_CONCURRENT_SHOWCASE_TICKS = 1;
const IDENTITY_RETRY_MS = [4_000, 16_000, 30_000];
const ZONE_RETRY_MS = [4_000, 16_000, 30_000];
const MEMBERSHIP_TICK_STORAGE_KEY = 'edgentik.showcase.membership.tick';
const MEMBERSHIP_LAST_STORAGE_KEY = 'edgentik.showcase.membership.last';

type MembershipTickRecord = { path: string; at: number };
type MembershipLastRecord = { path: string; at: number; payload: unknown };

let membershipTickMemory: MembershipTickRecord | null = null;
let membershipLastMemory: MembershipLastRecord | null = null;

function isShowcaseDocumentVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

function readStorageJson(key: string): unknown {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStorageJson(key: string, value: unknown): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / quota: in-memory gate still serializes this JS realm.
  }
}

export function resetShowcaseMembershipPollGateForTests(): void {
  membershipTickMemory = null;
  membershipLastMemory = null;
  try {
    localStorage.removeItem(MEMBERSHIP_TICK_STORAGE_KEY);
    localStorage.removeItem(MEMBERSHIP_LAST_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function claimMembershipHttpTick(path: string, now: number): boolean {
  const stored = readStorageJson(MEMBERSHIP_TICK_STORAGE_KEY) as MembershipTickRecord | null;
  const last = stored && typeof stored.at === 'number' ? stored : membershipTickMemory;
  if (last && last.path === path && now - last.at < SHOWCASE_MEMBERSHIP_POLL_MS) {
    return false;
  }
  const rec = { path, at: now };
  membershipTickMemory = rec;
  writeStorageJson(MEMBERSHIP_TICK_STORAGE_KEY, rec);
  return true;
}

function rememberMembershipPayload(path: string, payload: unknown, now: number): void {
  const rec = { path, at: now, payload };
  membershipLastMemory = rec;
  writeStorageJson(MEMBERSHIP_LAST_STORAGE_KEY, rec);
}

function lastSharedMembershipPayload(path: string, now: number): unknown | null {
  const stored = readStorageJson(MEMBERSHIP_LAST_STORAGE_KEY) as MembershipLastRecord | null;
  const rec = stored && stored.path ? stored : membershipLastMemory;
  if (!rec || rec.path !== path) return null;
  if (now - rec.at > SHOWCASE_MEMBERSHIP_POLL_MS * 2) return null;
  return rec.payload;
}

const unreadMembership = (): ShowcaseMembershipRead => ({
  readOk: false,
  qualifyingPresent: null,
  qualifyingCount: null,
});

/** Distinct link-table ids. Null means the id read itself failed. */
function distinctLinkedIncidentIds(payload: unknown): string[] | null {
  const body = payload && typeof payload === 'object'
    ? payload as { incident_ids?: unknown }
    : null;
  if (!body || !Array.isArray(body.incident_ids)) return null;
  const ids: string[] = [];
  const seen = new Set<string>();
  body.incident_ids.forEach((value) => {
    const id = String(value || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

const idleSnapshot = (config: ShowcaseLiveNowConfig): ShowcaseLiveNowSnapshot => ({
  phase: 'unknown',
  liveCanonicalActive: false,
  occupancy: null,
  occupancyReadOk: false,
  membershipReadOk: false,
  qualifyingPresent: null,
  buyerLiveReadOk: false,
  thresholdSeconds: envShowcaseLiveDefaults().thresholdSeconds,
  dwellElapsedSeconds: null,
  zonePolygon: null,
  zoneId: config.zoneId,
  cameraId: config.cameraId,
  matchingIncidentId: null,
  workflowStatus: null,
  areaName: null,
  conditionId: null,
});

function identityRow(condition: SiteConditionState): ShowcaseConditionIdentity {
  const cameraIds = [
    ...(condition.canonicalScope?.cameraIds || []),
    condition.operationalScope?.cameraId || '',
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const zoneIds = [
    ...(condition.canonicalScope?.zoneIds || []),
    condition.measurementDefinition?.gateZoneId || '',
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const policyIds = [
    ...(condition.canonicalScope?.policyIds || []),
    condition.measurementDefinition?.policyId || '',
  ].map((value) => String(value || '').trim()).filter(Boolean);
  return { conditionId: condition.conditionId, cameraIds, zoneIds, policyIds };
}

type IdentityResolution =
  | { key: string; status: 'pending' }
  | { key: string; status: 'selected'; conditionId: string }
  | { key: string; status: 'failed' };

type LiveTickInputs = {
  items: Awaited<ReturnType<typeof incidentsApi.getBuyerLiveAlerts>>['items'];
  buyerLiveReadOk: boolean;
  lifecycleReadOk: boolean;
  linkedIncidentIds: string[] | null;
};

function unavailableSnapshot(config: ShowcaseLiveNowConfig): ShowcaseLiveNowSnapshot {
  return assembleLiveNowSnapshot({
    config,
    items: [],
    buyerLiveReadOk: false,
    identityReadOk: false,
    lifecycleReadOk: false,
    linkedIncidentIds: null,
    zone: null,
    membership: unreadMembership(),
    occupancyStartedAtMs: null,
  });
}

export function useShowcaseLiveNow(args: {
  active: boolean;
  config: ShowcaseLiveNowConfig;
  resetNonce?: number;
}): ShowcaseLiveNowSnapshot {
  const [snapshot, setSnapshot] = useState<ShowcaseLiveNowSnapshot>(() => idleSnapshot(args.config));
  const [identity, setIdentity] = useState<IdentityResolution>(() => ({
    key: showcaseIdentityConfigKey(args.config),
    status: 'pending',
  }));
  const occupancyStartedAtMs = useRef<number | null>(null);
  const emptySinceMs = useRef<number | null>(null);
  const sawPhysicalActive = useRef(false);
  const identityKeyRef = useRef(showcaseIdentityConfigKey(args.config));
  const zoneIdRef = useRef(String(args.config.zoneId || '').trim());
  const zoneDefRef = useRef<Zone | null>(null);
  const lastTickInputsRef = useRef<LiveTickInputs | null>(null);
  const membershipRef = useRef<ShowcaseMembershipRead>(unreadMembership());
  const membershipScopeRef = useRef('');
  const pollGenerationRef = useRef(0);
  const membershipGenerationRef = useRef(0);
  const liveConfigRef = useRef<ShowcaseLiveNowConfig>(args.config);
  const selectedConditionIdRef = useRef<string | null>(null);

  const siteId = args.config.siteId;
  const cameraId = args.config.cameraId;
  const zoneId = args.config.zoneId;
  const policyId = args.config.policyId;
  const deviceId = args.config.deviceId;
  liveConfigRef.current = { siteId, cameraId, zoneId, policyId, deviceId };
  selectedConditionIdRef.current = identity.status === 'selected' && identity.key === showcaseIdentityConfigKey({
    siteId, cameraId, zoneId, policyId,
  })
    ? identity.conditionId
    : null;

  const publishCombined = (generation: number) => {
    if (pollGenerationRef.current !== generation) return;
    const inputs = lastTickInputsRef.current;
    setSnapshot(assembleLiveNowSnapshot({
      config: liveConfigRef.current,
      items: inputs?.items ?? [],
      buyerLiveReadOk: inputs?.buyerLiveReadOk ?? false,
      identityReadOk: true,
      lifecycleReadOk: inputs?.lifecycleReadOk ?? false,
      linkedIncidentIds: inputs?.linkedIncidentIds ?? null,
      zone: zoneDefRef.current,
      membership: membershipRef.current,
      envThresholdSeconds: envShowcaseLiveDefaults().thresholdSeconds,
      occupancyStartedAtMs: occupancyStartedAtMs.current,
      emptySinceMs: emptySinceMs.current,
      clearingFromOccupancy: sawPhysicalActive.current,
      conditionId: selectedConditionIdRef.current,
    }));
  };

  useEffect(() => {
    const key = showcaseIdentityConfigKey({ siteId, cameraId, zoneId, policyId });
    identityKeyRef.current = key;
    setIdentity({ key, status: 'pending' });
    if (!args.active) {
      return undefined;
    }
    const trimmedSiteId = String(siteId || '').trim();
    if (!trimmedSiteId) {
      setIdentity({ key, status: 'failed' });
      return undefined;
    }

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempt = 0;

    const resolveIdentity = async () => {
      let selectedId: string | null = null;
      try {
        const result = await fetchSiteConditionState({ siteId: trimmedSiteId, identityOnly: true });
        if (cancelled || identityKeyRef.current !== key) return;
        if (result.status === 'success' && result.data.identityOnly === true) {
          const selected = selectSiteConditionForBoothScope({
            conditions: result.data.conditions.map(identityRow),
            cameraId,
            zoneId,
            policyId,
          });
          if (selected.status === 'selected') {
            selectedId = selected.conditionId;
          }
        }
      } catch {
        selectedId = null;
      }
      if (cancelled || identityKeyRef.current !== key) return;
      if (selectedId) {
        setIdentity({ key, status: 'selected', conditionId: selectedId });
        return;
      }
      setIdentity({ key, status: 'failed' });
      const delay = IDENTITY_RETRY_MS[Math.min(attempt, IDENTITY_RETRY_MS.length - 1)];
      attempt += 1;
      retryTimer = window.setTimeout(() => { void resolveIdentity(); }, delay);
    };

    void resolveIdentity();
    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
    };
  }, [args.active, siteId, cameraId, zoneId, policyId]);

  useEffect(() => {
    const trimmedZoneId = String(zoneId || '').trim();
    zoneIdRef.current = trimmedZoneId;
    zoneDefRef.current = null;
    if (!args.active || !trimmedZoneId) {
      return undefined;
    }

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempt = 0;

    const resolveZone = async () => {
      let zone: Zone | null = null;
      try {
        zone = await zonesApi.getZone(trimmedZoneId);
      } catch {
        zone = null;
      }
      if (cancelled || zoneIdRef.current !== trimmedZoneId) return;
      if (zone) {
        zoneDefRef.current = zone;
        publishCombined(pollGenerationRef.current);
        return;
      }
      const delay = ZONE_RETRY_MS[Math.min(attempt, ZONE_RETRY_MS.length - 1)];
      attempt += 1;
      retryTimer = window.setTimeout(() => { void resolveZone(); }, delay);
    };

    void resolveZone();
    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
    };
  }, [args.active, zoneId]);

  useEffect(() => {
    occupancyStartedAtMs.current = null;
    lastTickInputsRef.current = null;
    const generation = pollGenerationRef.current + 1;
    pollGenerationRef.current = generation;
    const liveConfig: ShowcaseLiveNowConfig = { siteId, cameraId, zoneId, policyId, deviceId };
    if (!args.active) {
      setSnapshot(idleSnapshot(liveConfig));
      return undefined;
    }

    const currentKey = showcaseIdentityConfigKey(liveConfig);
    const selectedConditionId = identity.status === 'selected' && identity.key === currentKey
      ? identity.conditionId
      : null;
    if (!selectedConditionId) {
      setSnapshot(unavailableSnapshot(liveConfig));
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;
    let concurrentTicks = 0;
    const trimmedSiteId = String(siteId || '').trim();

    const tick = async () => {
      let items: LiveTickInputs['items'] = [];
      let buyerLiveReadOk = false;
      let lifecycleReadOk = false;
      let linkedIncidentIds: string[] | null = null;

      if (trimmedSiteId) {
        try {
          const response = await api.get(
            `/api/v1/sites/${encodeURIComponent(trimmedSiteId)}/condition-linked-incident-ids?condition_id=${encodeURIComponent(selectedConditionId)}`,
          );
          const ids = distinctLinkedIncidentIds(response?.data);
          if (ids) {
            linkedIncidentIds = ids;
            lifecycleReadOk = true;
          }
        } catch {
          lifecycleReadOk = false;
        }
      }

      try {
        const live = await incidentsApi.getBuyerLiveAlerts({ limit: 50 });
        buyerLiveReadOk = live.read_state === 'ok' || live.ok === true;
        items = live.items || [];
      } catch {
        buyerLiveReadOk = false;
      }
      if (cancelled || pollGenerationRef.current !== generation) return;
      lastTickInputsRef.current = { items, buyerLiveReadOk, lifecycleReadOk, linkedIncidentIds };
      publishCombined(generation);
    };

    const runLoop = async () => {
      if (cancelled || pollGenerationRef.current !== generation) return;
      if (concurrentTicks >= MAX_CONCURRENT_SHOWCASE_TICKS) return;
      concurrentTicks += 1;
      try {
        await tick();
      } finally {
        concurrentTicks -= 1;
      }
      if (cancelled || pollGenerationRef.current !== generation) return;
      timer = window.setTimeout(() => { void runLoop(); }, SHOWCASE_LIVE_POLL_MS);
    };

    void runLoop();
    return () => {
      cancelled = true;
      lastTickInputsRef.current = null;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [args.active, args.resetNonce, identity, siteId, cameraId, zoneId, policyId, deviceId]);

  useEffect(() => {
    const liveConfig: ShowcaseLiveNowConfig = { siteId, cameraId, zoneId, policyId, deviceId };
    const scopeKey = showcaseMembershipScopeKey(liveConfig);
    if (membershipScopeRef.current !== scopeKey) {
      membershipRef.current = unreadMembership();
      occupancyStartedAtMs.current = null;
      emptySinceMs.current = null;
      sawPhysicalActive.current = false;
      membershipScopeRef.current = scopeKey;
    }
    const generation = membershipGenerationRef.current + 1;
    membershipGenerationRef.current = generation;

    if (!args.active) {
      return undefined;
    }

    const currentIdentityKey = showcaseIdentityConfigKey(liveConfig);
    const identityReady = identity.status === 'selected' && identity.key === currentIdentityKey;
    const trimmedDeviceId = String(deviceId || '').trim();
    const trimmedCameraId = String(cameraId || '').trim();
    const trimmedZoneId = String(zoneId || '').trim();
    if (!identityReady || !trimmedDeviceId || !trimmedCameraId || !trimmedZoneId) {
      publishCombined(pollGenerationRef.current);
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;
    let concurrentTicks = 0;
    const path = `/api/v1/devices/${encodeURIComponent(trimmedDeviceId)}/cameras/${encodeURIComponent(trimmedCameraId)}/zones/${encodeURIComponent(trimmedZoneId)}/qualifying-membership`;

    const applyMembership = (next: ShowcaseMembershipRead) => {
      if (cancelled || membershipGenerationRef.current !== generation) return;
      if (showcaseMembershipScopeKey(liveConfigRef.current) !== scopeKey) return;
      membershipRef.current = next;
      if (next.readOk && next.qualifyingPresent === true) {
        emptySinceMs.current = null;
        if (occupancyStartedAtMs.current == null) occupancyStartedAtMs.current = Date.now();
        const entry = resolveThresholdSeconds({
          zone: zoneDefRef.current,
          envSeconds: envShowcaseLiveDefaults().thresholdSeconds,
        });
        const occupiedFor = occupancyStartedAtMs.current == null
          ? 0
          : (Date.now() - occupancyStartedAtMs.current) / 1000;
        if (entry != null && occupiedFor >= entry) sawPhysicalActive.current = true;
      } else if (next.readOk && next.qualifyingPresent === false) {
        occupancyStartedAtMs.current = null;
        if (emptySinceMs.current == null) emptySinceMs.current = Date.now();
      }
      publishCombined(pollGenerationRef.current);
    };

    const membershipFromShared = (payload: unknown): ShowcaseMembershipRead => (
      membershipFromProbe({
        payload,
        deviceId: trimmedDeviceId,
        cameraId: trimmedCameraId,
        zoneId: trimmedZoneId,
      })
    );

    const tick = async () => {
      const now = Date.now();
      if (!isShowcaseDocumentVisible() || !claimMembershipHttpTick(path, now)) {
        const shared = lastSharedMembershipPayload(path, now);
        applyMembership(shared ? membershipFromShared(shared) : membershipRef.current);
        return;
      }
      let next = unreadMembership();
      try {
        const response = await api.get(path);
        rememberMembershipPayload(path, response?.data, Date.now());
        next = membershipFromShared(response?.data);
      } catch {
        next = membershipRef.current.readOk ? membershipRef.current : unreadMembership();
      }
      applyMembership(next);
    };

    const runLoop = async () => {
      if (cancelled || membershipGenerationRef.current !== generation) return;
      if (concurrentTicks >= MAX_CONCURRENT_SHOWCASE_TICKS) return;
      concurrentTicks += 1;
      try {
        await tick();
      } finally {
        concurrentTicks -= 1;
      }
      if (cancelled || membershipGenerationRef.current !== generation) return;
      timer = window.setTimeout(() => { void runLoop(); }, SHOWCASE_MEMBERSHIP_POLL_MS);
    };

    void runLoop();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [args.active, args.resetNonce, identity, siteId, cameraId, zoneId, policyId, deviceId]);

  return snapshot;
}
